/**
 * Hooks over the platform feature flags (`{key: bool}` for the user-facing
 * ones; see `featureFlags.js` for what they are and are not).
 *
 * The signed-in user payload already carries the map (`user.featureFlags`,
 * from the backend's `feature_flags`), so the first paint after sign-in
 * decides from it instead of waiting: the map seeds the query as initial
 * data. `GET /api/core/features/` then refreshes it once that seed is older
 * than the server's 60-second cache. Without a seed (signed out) every flag
 * reads as off until the endpoint answers.
 */
import { useQuery } from '@tanstack/react-query'
import { FEATURE_FLAGS } from '@/endpoints'
import useAuth from '@/hooks/useAuth'
import axios from '@/lib/axios'
import { FEATURE_FLAGS_QUERY_KEY, isPlatformFeatureOn } from './featureFlags'

const STALE_TIME_MS = 60 * 1000

/** The React Query result for the map, seeded from the signed-in user payload. */
export function useFeatureFlagsQuery() {
    const { user } = useAuth()
    const seed = user?.featureFlags
    return useQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEY,
        queryFn: async () => (await axios.get(FEATURE_FLAGS)).data,
        initialData: seed ?? undefined,
        staleTime: STALE_TIME_MS,
        retry: false
    })
}

/**
 * `{ flags, isLoading, isEnabled }`: the `{key: bool}` map (`undefined` while
 * loading or after a failure), whether the first answer is still pending, and
 * a lookup where loading, unknown or failed all read as off.
 */
export function useFeatureFlags() {
    const query = useFeatureFlagsQuery()
    const flags = query.data
    return {
        flags,
        isLoading: query.isPending,
        isEnabled: (key) => isPlatformFeatureOn(flags, key)
    }
}

/** Whether one user-facing platform feature is on. Loading, unknown or failed = off. */
export function useFeatureEnabled(key) {
    return useFeatureFlags().isEnabled(key)
}
